import _keyBy from 'lodash/keyBy';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isZoomSyncGateError } from '../helpers/utils';
import { useSyncMeetingAttendanceFromZoomMutation } from '../queries/enhanced';
import { SyncMeetingAttendanceFromZoomMutation } from '../queries/sync-meeting-attendance-from-zoom.generated';

export type ZoomSyncResult = SyncMeetingAttendanceFromZoomMutation['syncMeetingAttendanceFromZoom'];
export type MatchedZoomParticipant = ZoomSyncResult['matchedParticipants'][number];
export type UnmatchedZoomParticipant = ZoomSyncResult['unmatchedParticipants'][number];

// Syncs a meeting's attendance from Zoom. The first time an ended meeting is
// opened the sync runs on its own; sync() re-runs it on demand. The server write
// is idempotent, so a repeat sync never changes attendance a second time.
//
// The automatic run stays quiet when there is simply nothing to sync yet (for
// example Zoom has no record of the occurrence); those errors only surface when
// the facilitator syncs by hand. Any other failure is surfaced either way.
export function useZoomAttendanceSync(meetingId: string, canAutoSync: boolean) {
  const [syncAttendance, { isLoading }] = useSyncMeetingAttendanceFromZoomMutation();
  const [result, setResult] = useState<ZoomSyncResult>();
  const [error, setError] = useState<unknown>();
  const autoSyncedMeetingId = useRef<string>();

  const runSync = useCallback(
    async (manual: boolean): Promise<ZoomSyncResult | undefined> => {
      setError(undefined);
      const response = await syncAttendance({ meetingId });
      if ('error' in response) {
        if (manual || !isZoomSyncGateError(response.error)) {
          setError(response.error);
        }
        return undefined;
      }
      setResult(response.data.syncMeetingAttendanceFromZoom);
      return response.data.syncMeetingAttendanceFromZoom;
    },
    [meetingId, syncAttendance]
  );

  useEffect(() => {
    if (!canAutoSync || !meetingId || autoSyncedMeetingId.current === meetingId) return;
    autoSyncedMeetingId.current = meetingId;
    runSync(false);
  }, [canAutoSync, meetingId, runSync]);

  const matchedByUserKey = useMemo(() => _keyBy(result?.matchedParticipants, 'userKey'), [result]);

  return {
    result,
    matchedByUserKey,
    error,
    isSyncing: isLoading,
    sync: useCallback(() => runSync(true), [runSync]),
  };
}
